import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

interface SEOAnalysisRequest {
  content: string;
  contentType: 'press_release' | 'blog_post' | 'social_media';
  targetKeywords?: string[];
  clientId?: string;
}

interface KeywordAnalysis {
  keyword: string;
  density: number;
  frequency: number;
  prominence: number;
}

interface ReadabilityMetrics {
  fleschKincaidGrade: number;
  averageSentenceLength: number;
  averageSyllablesPerWord: number;
  readingTime: number;
}

interface StructureAnalysis {
  headingStructure: {
    h1Count: number;
    h2Count: number;
    h3Count: number;
    hasProperHierarchy: boolean;
  };
  paragraphCount: number;
  averageParagraphLength: number;
  bulletPointsUsed: boolean;
  wordCount: number;
  characterCount: number;
}

interface SEOAnalysisResult {
  score: number;
  analysis: {
    keywordDensity: Record<string, number>;
    readability: ReadabilityMetrics;
    structure: StructureAnalysis;
    suggestions: string[];
    schemaMarkup?: string;
    metaDescription?: string;
    titleOptimization?: string;
    keywordAnalysis?: KeywordAnalysis[];
  };
  metadata: {
    analysisDate: string;
    contentType: string;
    wordCount: number;
    processingTime: number;
  };
}

export const useSEOAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<SEOAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyzeContent = useCallback(async (
    content: string,
    contentType: 'press_release' | 'blog_post' | 'social_media',
    targetKeywords: string[] = [],
    clientId?: string
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Validate input
      if (!content || content.trim().length === 0) {
        throw new Error('Content is required for analysis');
      }

      if (content.length < 50) {
        throw new Error('Content must be at least 50 characters long for meaningful analysis');
      }

      // Call the Firebase function
      const analyzeContentSEO = httpsCallable<SEOAnalysisRequest, SEOAnalysisResult>(
        functions, 
        'analyzeContentSEO'
      );

      const result = await analyzeContentSEO({
        content,
        contentType,
        targetKeywords,
        clientId
      });

      const analysisResult = result.data;
      setAnalysis(analysisResult);
      return analysisResult;

    } catch (error: any) {
      console.error('SEO analysis failed:', error);
      const errorMessage = error.message || 'Failed to analyze content. Please try again.';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError(null);
  }, []);

  const getScoreColor = useCallback((score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  }, []);

  const getScoreLevel = useCallback((score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  }, []);

  const getSuggestionsByPriority = useCallback((suggestions: string[]) => {
    if (!suggestions || suggestions.length === 0) return { high: [], medium: [], low: [] };

    // Simple categorization based on keywords in suggestions
    const high: string[] = [];
    const medium: string[] = [];
    const low: string[] = [];

    suggestions.forEach(suggestion => {
      const suggestionLower = suggestion.toLowerCase();
      
      if (
        suggestionLower.includes('title') ||
        suggestionLower.includes('heading') ||
        suggestionLower.includes('keyword') ||
        suggestionLower.includes('meta description')
      ) {
        high.push(suggestion);
      } else if (
        suggestionLower.includes('structure') ||
        suggestionLower.includes('readability') ||
        suggestionLower.includes('paragraph')
      ) {
        medium.push(suggestion);
      } else {
        low.push(suggestion);
      }
    });

    return { high, medium, low };
  }, []);

  return {
    // State
    loading,
    analysis,
    error,

    // Actions
    analyzeContent,
    clearAnalysis,

    // Helper functions
    getScoreColor,
    getScoreLevel,
    getSuggestionsByPriority,

    // Computed values
    score: analysis?.score || 0,
    hasAnalysis: !!analysis,
    suggestions: analysis?.analysis?.suggestions || [],
    keywordAnalysis: analysis?.analysis?.keywordAnalysis || [],
    readability: analysis?.analysis?.readability,
    structure: analysis?.analysis?.structure,
    metaDescription: analysis?.analysis?.metaDescription,
    titleOptimization: analysis?.analysis?.titleOptimization,
    schemaMarkup: analysis?.analysis?.schemaMarkup
  };
};

export default useSEOAnalysis;